import React from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  DeleteIntegrationConnectionDocument,
  IntegrationCatalogDocument,
  GetIntegrationRemoteOptionsDocument,
  IntegrationConnectionCredentialsDocument,
  RefreshIntegrationRemoteOptionsDocument,
  RotateIntegrationConnectionWebhookDocument,
  SaveIntegrationConnectionDocument,
  SetIntegrationConnectionStatusDocument,
  StartIntegrationOAuthDocument,
  TestIntegrationConnectionDocument,
  UpdateIntegrationConnectionCredentialsDocument,
} from "#app/lib/data";
import { toastManager } from "@salestrak/ui";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useResetOnChange } from "#app/lib/hooks/use-reset-on-change";

import type { IntegrationConnection, IntegrationProvider } from "./integrations-data";
import {
  canonicalFieldValue,
  seedValuesFromFields,
  settingsDirty,
  stableStringify,
} from "./connection-config-utils";

// Data + form logic for the integration connection config surfaces. Apollo and
// toasts live here (the mutation-hook layer), leaving the config views purely
// presentational — components call these hooks, hooks call Apollo.

/**
 * Start an OAuth authorization for a redirect-based provider, then hand the
 * browser to the provider's consent screen.
 *
 * A FULL-PAGE navigation (`location.assign`), not `window.open`. A popup is the
 * usual instinct, but it needs the opener to poll or listen for a postMessage to
 * know it finished, and popup blockers fire on anything not directly inside a
 * click handler. The whole flow already ends in a server redirect back to this
 * app, so a top-level navigation needs no channel back at all — the app simply
 * reloads with the outcome in the URL.
 *
 * Losing the unsaved dialog state is acceptable, and is why `name` is collected
 * before the redirect rather than after: nothing else in the form survives the
 * round trip, so nothing else may be required to complete it.
 *
 * `connectionId` set → RECONNECT (replace the token on that connection);
 * absent → create a new connection named `name`.
 */
export function useStartIntegrationOAuth() {
  const [startOAuth, { loading: starting }] = useMutation(StartIntegrationOAuthDocument);

  const handleStart = async (input: {
    provider: string;
    name: string;
    connectionId?: string;
  }) => {
    try {
      const res = await startOAuth({
        variables: {
          input: {
            provider: input.provider,
            name: input.name,
            connectionId: input.connectionId ?? null,
          },
        },
      });
      const url = res.data?.startIntegrationOAuth.authorizeUrl;
      if (!url) {
        toastManager.add({ title: "Failed to start authorization", type: "error" });
        return;
      }
      if (typeof window !== "undefined") window.location.assign(url);
    } catch (err) {
      toastMutationError(err, "Failed to start authorization");
    }
  };

  return { starting, handleStart };
}

/**
 * One browsable collection for the Features tab: the rows it holds, how fresh
 * they are, and a re-sync that starts a fresh read of the whole connection.
 *
 * Search is SERVER-side, and that is not a preference. The provider's own API
 * may have no search at all, so core
 * syncs the account into its own store and searches THAT. The stored directory
 * is far larger than one response carries — that is the point of it — so
 * filtering locally over the rows already fetched would silently search a subset
 * and report "no matches" for something that exists.
 *
 * `variables` is memoized: an inline object is a new reference every render and
 * makes Apollo re-subscribe forever.
 */

/**
 * How often to re-ask while a directory is still being read.
 *
 * The walk runs on the server, so nothing in this tab learns it finished
 * without asking. Two seconds is slow enough to be cheap (the query reads a
 * local table, never the provider) and fast enough that a small account's sync
 * looks immediate.
 */
const SYNC_POLL_MS = 2000;

export function useRemoteCollection(connectionId: string, source: string, search: string) {
  const variables = React.useMemo(
    () => ({ connectionId, source, search: search.trim() || null }),
    [connectionId, source, search],
  );
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    GetIntegrationRemoteOptionsDocument,
    { variables, fetchPolicy: "cache-and-network" },
  );
  const [refresh] = useMutation(RefreshIntegrationRemoteOptionsDocument);

  const result = data?.integrationRemoteOptions;
  const syncing = result?.syncing ?? false;

  // Poll only while a walk is actually running, and stop the moment it is not.
  // A permanent poll would re-query a table nobody is changing, and an admin
  // leaving this tab open all day is the normal case.
  React.useEffect(() => {
    if (!syncing) {
      stopPolling();
      return;
    }
    startPolling(SYNC_POLL_MS);
    return () => stopPolling();
  }, [syncing, startPolling, stopPolling]);

  const handleRefresh = async () => {
    try {
      const res = await refresh({ variables: { input: { connectionId, source } } });
      const outcome = res.data?.refreshIntegrationRemoteOptions;
      if (!outcome?.ok) {
        toastManager.add({ title: outcome?.error || "Failed to re-sync", type: "error" });
        return;
      }
      // The walk runs in the BACKGROUND, so there is nothing to await — the
      // refetch below only picks up `syncing: true`, which starts the poll that
      // shows it progressing. Awaiting a walk here is what the button used to
      // imply and could never deliver: a large account takes far longer than a
      // request, so the spinner ended over an unchanged list and the admin
      // clicked again, starting another walk over the same rate limit that
      // message delivery draws on.
      //
      // The mutation re-syncs EVERY collection on this connection, not the one
      // whose button was pressed. The sources are not independent — the
      // send-message picker reads a view over channels and people together — so
      // refreshing one and leaving the other stale made this button report
      // success while the list an author actually picks from did not change.
      await refetch();
      toastManager.add({ title: "Re-sync started", type: "success" });
    } catch (err) {
      toastMutationError(err, "Failed to re-sync");
    }
  };

  return {
    options: result?.options ?? [],
    truncated: result?.truncated ?? false,
    // A transport failure reads the same way to an admin as the provider
    // refusing: one sentence, and a list they can still scroll.
    error: result?.error ?? (error ? "Could not load this list." : null),
    // Never raw `loading`: the policy is cache-and-network, so it flips true on
    // every refetch and would blank a list that is already on screen.
    initialLoading: loading && !data,
    // The SERVER is still walking — distinct from `initialLoading`, which is
    // only about this request. The button stays busy for the whole walk rather
    // than for the millisecond the mutation takes.
    refreshing: syncing,
    lastSyncedAt: result?.lastSyncedAt ?? null,
    totalCount: result?.totalCount ?? 0,
    ceilingHit: result?.ceilingHit ?? false,
    handleRefresh,
  };
}

/**
 * New-configuration form state: name + credential values, a two-step
 * Test→Save, and the mutations behind them. On a passing save it invokes
 * `onSaved` with the new connection id.
 */
export function useNewConnectionForm(
  provider: IntegrationProvider,
  onSaved: (connectionId: string) => void,
) {
  const [name, setName] = React.useState("");
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [tested, setTested] = React.useState(false);
  const [testConnection, { loading: testing }] = useMutation(TestIntegrationConnectionDocument);
  const [saveConnection, { loading: saving }] = useMutation(SaveIntegrationConnectionDocument);

  // Any edit invalidates a prior passing test.
  const setField = (key: string, next: string) => {
    setValues((prev) => ({ ...prev, [key]: next }));
    setTested(false);
  };
  const setNameField = (next: string) => {
    setName(next);
    setTested(false);
  };

  // Only required fields gate save; optional inputs (required === false) may be blank.
  const allFilled =
    name.trim().length > 0 &&
    provider.authFields
      .filter((f) => f.required)
      .every((f) => (values[f.key] ?? "").trim().length > 0);

  const credentialValues = () =>
    provider.authFields.map((f) => ({
      key: f.key,
      value: canonicalFieldValue(f, values[f.key] ?? ""),
    }));

  const handleTest = async () => {
    try {
      const res = await testConnection({
        variables: { input: { provider: provider.id, values: credentialValues() } },
      });
      const result = res.data?.testIntegrationConnection;
      if (result?.ok) {
        setTested(true);
        toastManager.add({ title: "Connection successful", type: "success" });
      } else {
        toastManager.add({ title: result?.error || "Connection test failed", type: "error" });
      }
    } catch (err) {
      toastMutationError(err, "Failed to test connection");
    }
  };

  const handleSave = async () => {
    try {
      const res = await saveConnection({
        variables: { input: { provider: provider.id, name, values: credentialValues() } },
        // Reload the catalog so the new configuration + updated card count show.
        refetchQueries: [{ query: IntegrationCatalogDocument }],
        awaitRefetchQueries: true,
      });
      const result = res.data?.saveIntegrationConnection;
      if (result?.ok && result.connection) {
        toastManager.add({ title: "Connection saved", type: "success" });
        onSaved(result.connection.id);
      } else {
        toastManager.add({ title: result?.error || "Failed to save connection", type: "error" });
      }
    } catch (err) {
      toastMutationError(err, "Failed to save connection");
    }
  };

  return {
    name,
    setNameField,
    values,
    setField,
    tested,
    testing,
    saving,
    allFilled,
    handleTest,
    handleSave,
  };
}

/**
 * Settings-tab state: loads the stored credential preview, tracks unsaved edits
 * against that on-load baseline (`isDirty` / `handleReset`), and owns the
 * two-step Test→Save credential update.
 */
export function useConnectionSettings({
  providerId,
  connectionId,
  initialName,
}: {
  providerId: string;
  connectionId: string;
  initialName: string;
}) {
  // Memoized so the query variables keep a stable ref across renders.
  const variables = React.useMemo(() => ({ connectionId }), [connectionId]);
  const { data, loading } = useQuery(IntegrationConnectionCredentialsDocument, {
    variables,
    fetchPolicy: "cache-and-network",
  });
  const fields = data?.integrationConnectionCredentials.fields ?? [];
  // The external account the stored credentials are bound to. Read-only, so it
  // never joins `values` or the dirty check.
  const accountLabel = data?.integrationConnectionCredentials.accountLabel ?? null;
  // The guide RESOLVED for this connection. Only this one can name an
  // install-specific value, which can differ per environment;
  // the provider's static guide is the fallback the server already applied.
  //
  // `command` is normalized null → undefined like every other nullable crossing
  // into a view type, so this matches the shape the catalog's guide already has.
  const raw = data?.integrationConnectionCredentials.connectionGuide;
  const connectionGuide = raw ? { ...raw, command: raw.command ?? undefined } : null;
  // The stored blob didn't decrypt, so every field above arrived blank. The form
  // still renders (the field list comes from the provider definition) and saving
  // overwrites the bad blob — the tab is the repair path, so it warns rather
  // than hides.
  const credentialsUnreadable =
    data?.integrationConnectionCredentials.credentialsUnreadable ?? false;
  const [name, setName] = React.useState(initialName);
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [tested, setTested] = React.useState(false);
  const [testConnection, { loading: testing }] = useMutation(TestIntegrationConnectionDocument);
  const [updateCredentials, { loading: saving }] = useMutation(
    UpdateIntegrationConnectionCredentialsDocument,
  );

  // Seed prefill values once the preview loads (secret fields arrive blank;
  // list fields seed as their newline editing buffer). Re-seed only when the
  // connection identity or the stored fields actually change — NOT on every
  // `data` ref churn (a background cache-and-network revalidation returning the
  // same preview would otherwise discard the user's in-progress credential
  // edits). Mirrors useConnectionSetup's hardened reset.
  useResetOnChange(
    data ? `${connectionId}|${stableStringify(data.integrationConnectionCredentials.fields)}` : null,
    () => setValues(seedValuesFromFields(data!.integrationConnectionCredentials.fields)),
  );

  // Any edit invalidates a prior passing test (mirrors the new-connection flow).
  const setField = (key: string, next: string) => {
    setValues((prev) => ({ ...prev, [key]: next }));
    setTested(false);
  };
  const setNameField = (next: string) => {
    setName(next);
    setTested(false);
  };

  // Unsaved-changes check against the on-load baseline: the name it loaded with
  // and each field's seeded value (secret fields seed blank, so typing one marks
  // the form dirty; list fields compare their newline buffer, so adding/removing
  // a row — even a blank one — marks the form dirty too).
  const isDirty = settingsDirty(fields, values, name, initialName);

  // Revert to that baseline: re-seed values from the loaded fields, restore the
  // name, and re-arm the two-step Test.
  const handleReset = () => {
    setValues(seedValuesFromFields(fields));
    setName(initialName);
    setTested(false);
  };

  const credentialValues = () =>
    fields.map((f) => ({ key: f.key, value: canonicalFieldValue(f, values[f.key] ?? "") }));

  const handleTest = async () => {
    try {
      const res = await testConnection({
        variables: {
          // connectionId → the server merges these over the stored creds, so
          // blank secret fields keep their existing value.
          input: { provider: providerId, connectionId, values: credentialValues() },
        },
      });
      const result = res.data?.testIntegrationConnection;
      if (result?.ok) {
        setTested(true);
        toastManager.add({ title: "Connection successful", type: "success" });
      } else {
        toastManager.add({ title: result?.error || "Connection test failed", type: "error" });
      }
    } catch (err) {
      toastMutationError(err, "Failed to test connection");
    }
  };

  const handleSave = async () => {
    try {
      const res = await updateCredentials({
        variables: { input: { connectionId, name, values: credentialValues() } },
        refetchQueries: [
          { query: IntegrationConnectionCredentialsDocument, variables: { connectionId } },
          { query: IntegrationCatalogDocument },
        ],
        awaitRefetchQueries: true,
      });
      const result = res.data?.updateIntegrationConnectionCredentials;
      if (result?.ok) {
        toastManager.add({ title: "Changes saved", type: "success" });
        // Reset back to the on-load state: blank the secret inputs so they show
        // the refreshed last-4 hint again, and re-arm the two-step Test.
        setValues((prev) => {
          const next = { ...prev };
          for (const f of fields) if (f.secret) next[f.key] = "";
          return next;
        });
        setTested(false);
      } else {
        toastManager.add({ title: result?.error || "Failed to save changes", type: "error" });
      }
    } catch (err) {
      toastMutationError(err, "Failed to save changes");
    }
  };

  return {
    fields,
    accountLabel,
    connectionGuide,
    credentialsUnreadable,
    initialLoading: loading && !data,
    name,
    setNameField,
    values,
    setField,
    tested,
    testing,
    saving,
    isDirty,
    handleTest,
    handleSave,
    handleReset,
  };
}

/**
 * Active/Disabled status toggle for a persisted connection. Optimistic, with a
 * rollback + toast on failure.
 */
export function useConnectionStatus(connection: IntegrationConnection) {
  const [active, setActive] = React.useState(connection.active);
  const [setConnectionStatus, { loading: statusSaving }] = useMutation(
    SetIntegrationConnectionStatusDocument,
  );

  const handleStatus = async (next: boolean) => {
    setActive(next); // optimistic
    try {
      const res = await setConnectionStatus({
        variables: { input: { connectionId: connection.id, active: next } },
        refetchQueries: [{ query: IntegrationCatalogDocument }],
      });
      const result = res.data?.setIntegrationConnectionStatus;
      if (!result?.ok) {
        setActive(connection.active);
        toastManager.add({ title: result?.error || "Failed to update status", type: "error" });
      }
    } catch (err) {
      setActive(connection.active);
      toastMutationError(err, "Failed to update status");
    }
  };

  return { active, statusSaving, handleStatus };
}

/**
 * Rotate an inbound connection's webhook token. The old URL stops resolving
 * immediately, so the caller confirms first. Refetches the catalog so the new
 * URL surfaces everywhere.
 */
// The same mutation both ROTATES an existing webhook URL and GENERATES the first
// one for a connection that predates token minting — it re-runs the provider's
// `registerWebhookSubscription` either way (which, for a subscription-based CRM,
// registers the URL remotely rather than just minting a local string). Only the
// copy differs, so the caller says which case it is.
export function useRotateWebhook(connectionId: string, { isFirst = false } = {}) {
  const [rotate, { loading: rotating }] = useMutation(
    RotateIntegrationConnectionWebhookDocument,
  );
  const verb = isFirst ? "generate" : "rotate";

  const handleRotate = async () => {
    try {
      const res = await rotate({
        variables: { input: { connectionId } },
        refetchQueries: [{ query: IntegrationCatalogDocument }],
        awaitRefetchQueries: true,
      });
      const result = res.data?.rotateIntegrationConnectionWebhook;
      if (result?.ok) {
        toastManager.add({
          title: isFirst ? "Webhook URL generated" : "Webhook URL rotated",
          type: "success",
        });
      } else {
        toastManager.add({ title: result?.error || `Failed to ${verb} the webhook URL`, type: "error" });
      }
    } catch (err) {
      toastMutationError(err, `Failed to ${verb} the webhook URL`);
    }
  };

  return { rotating, handleRotate };
}

/**
 * Delete a saved connection (and its stored credentials). Refetches the catalog
 * so the connection disappears from every list; `onDeleted` runs only on
 * success, so the caller can close its confirm UI.
 */
export function useDeleteConnection(connectionId: string, onDeleted: () => void) {
  const [deleteConnection, { loading: deleting }] = useMutation(
    DeleteIntegrationConnectionDocument,
  );

  const handleDelete = async () => {
    try {
      const res = await deleteConnection({
        variables: { input: { connectionId } },
        refetchQueries: [{ query: IntegrationCatalogDocument }],
        awaitRefetchQueries: true,
      });
      const result = res.data?.deleteIntegrationConnection;
      if (result?.ok) {
        toastManager.add({ title: "Connection deleted", type: "success" });
        onDeleted();
      } else {
        toastManager.add({
          title: result?.error || "Failed to delete connection",
          type: "error",
        });
      }
    } catch (err) {
      toastMutationError(err, "Failed to delete connection");
    }
  };

  return { deleting, handleDelete };
}
